<template lang="pug">
.pdf-page(
  :class='{ loaded: loaded }'
  :id='`page-${proxyPage.pageNumber}`'
  :style='{ height: `${height}px`, width: `${width}px` }'
)
  // @slot Default slot for the component
  slot
</template>

<script>
import PDFPageProxy from 'pdfjs-dist';

/**
 * @displayName PageMobile
 **/
export default {
  name: 'PdfPageMobile',
  props: {
    /** PDFPageProxy instance */
    proxyPage: {
      type: PDFPageProxy,
      required: true,
    },
    /** Scale for rendering the page */
    scale: {
      type: Number,
      default: 1,
    },
  },
  data() {
    return {
      loaded: false,
      height: 0,
      width: 0,
      viewport: null,
      context: null,
      originalWidth: 0,
      originalHeight: 0,
    };
  },
  watch: {
    scale: {
      handler() {
        this.loaded = false;
        this.removeCanvas();
        this.render();
      },
      immediate: false,
    },
  },
  created() {
    this.originalWidth = this.proxyPage.view[2];
    this.originalHeight = this.proxyPage.view[3];
    this.width = this.originalWidth;
    this.height = this.originalHeight;
  },
  mounted() {
    this.render();
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting === true) {
          if (!this.loaded) {
            this.render();
            return;
          }
          this.verifyRender();
        }
      },
      { threshold: [0] }
    );
    observer.observe(this.$el);
  },
  methods: {
    render() {
      if (!this.proxyPage) return;
      this.viewport = this.proxyPage.getViewport({ scale: this.scale });
      const canvas = document.createElement('canvas');
      this.$el.appendChild(canvas);
      canvas.height = this.viewport.height;
      canvas.width = this.viewport.width;
      this.height = this.viewport.height;
      this.width = this.viewport.width;
      this.context = canvas.getContext('2d');
      this.proxyPage.render({
        canvasContext: this.context,
        viewport: this.viewport,
      });
      this.loaded = true;

      // Emit scale and page size information
      this.$emit('scale', this.scale);
      this.$emit('page-size', {
        originalWidth: this.originalWidth,
        originalHeight: this.originalHeight,
        scaledWidth: this.width,
        scaledHeight: this.height,
      });
      this.$emit('loaded', this.proxyPage.pageNumber);
    },
    verifyRender() {
      if (!this.context._transformMatrix) {
        this.removeCanvas();
        this.render();
      }
    },
    removeCanvas() {
      while (this.$el.lastChild && this.$el.lastChild.tagName === 'CANVAS') {
        this.$el.removeChild(this.$el.lastChild);
      }
    },
    calculatePosition(x, y) {
      // Returning scaled coordinates based on the page's top-left corner
      return {
        x: x * this.scale,
        y: y * this.scale,
      };
    },
  },
};
</script>

<style lang="stylus">
.pdf-page
  position: relative
  margin: 0 auto
  background: white
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1)
  border-radius: 8px
  overflow: visible
  transform-origin: center center

  canvas
    display: block
    transform-origin: center center
    transition: transform 0.2s ease-out
    image-rendering: -webkit-optimize-contrast
    image-rendering: crisp-edges

  .document-element-viewer
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    pointer-events: none

    .document-element
      position: absolute
      pointer-events: auto
      transition: transform 0.2s ease-out
      transform-origin: center center

@media (max-width: 576px)
  .pdf-page
    border-radius: 4px
    margin-bottom: 8px

    canvas
      transform-origin: center center !important

    .document-element-viewer
      .document-element
        transform-origin: center center !important

@media (max-width: 375px)
  .pdf-page
    border-radius: 2px
    margin-bottom: 4px
</style>
